/**
 * Movement and battle choreography.
 *
 * A tiny keyframe system drives everything: `Timeline` holds tracks that each
 * own a target object, a property path, and a list of `{ t, value }` keys.
 * Tracks interpolate with a configurable easing and are sampled every frame.
 * Battle sequences are just longer timelines with a `strike` callback that
 * fires the moment a blow lands, so the victim's death animation and the
 * hit-spark particles stay in sync with the attacker's swing.
 */

import * as THREE from 'three';
import { DebrisField } from './debris.js';
import { damageList, isMismatched, resolveSlot, ATTACKER_LANCE } from './damage.js';
import {
  resolveImpact, resolveLanding, IMPACTOR_BY_MANNER, PIECE_SOUND, POOL_BY_TYPE
} from '../audio/soundboard.js';
import {
  damagePart, setBookOpen, stowCannon, CANNON_STOWED, CANNON_RUN_OUT
} from './pieces.js';
import { squareToWorld } from './board.js';

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const easeIn = (t) => t * t * t;
const linear = (t) => t;
const EASINGS = { easeInOut, easeOut, easeIn, linear };

// Cues that report game state rather than a blow landing. These still play
// while a battle is being skipped; the combat cues do not, or a skipped fight
// fires every impact a few milliseconds apart.
const LOUD_CUES = new Set(['win', 'lose', 'check', 'move']);

/** Places a sound across the stereo field by where it happens on the board. */
const panAt = (position) => Math.max(-1, Math.min(1, position.x / 4)) * 0.7;

class Track {
  constructor(target, path, keys, easing = 'easeInOut') {
    this.target = target;
    this.path = path.split('.');
    this.keys = keys.slice().sort((a, b) => a.t - b.t);
    this.ease = EASINGS[easing] ?? easeInOut;
  }

  resolve() {
    let obj = this.target;
    for (let i = 0; i < this.path.length - 1; i++) obj = obj?.[this.path[i]];
    return obj;
  }

  sample(time) {
    const obj = this.resolve();
    if (!obj) return;
    const prop = this.path[this.path.length - 1];
    const keys = this.keys;

    if (time <= keys[0].t) {
      obj[prop] = keys[0].value;
      return;
    }
    const last = keys[keys.length - 1];
    if (time >= last.t) {
      obj[prop] = last.value;
      return;
    }
    for (let i = 0; i < keys.length - 1; i++) {
      const a = keys[i];
      const b = keys[i + 1];
      if (time >= a.t && time <= b.t) {
        const span = b.t - a.t;
        const k = span === 0 ? 1 : this.ease((time - a.t) / span);
        obj[prop] = a.value + (b.value - a.value) * k;
        return;
      }
    }
  }
}

class Timeline {
  constructor(duration) {
    this.duration = duration;
    this.tracks = [];
    this.events = [];
    this.time = 0;
    this.done = false;
    // Playback speed. Skipping ramps this up rather than jumping straight to
    // the end, so every `at()` event still fires, in order — a jump would
    // discharge a whole battle's worth of impacts in a single frame.
    this.rate = 1;
    this.targetRate = 1;
  }

  /** Fast-forwards to the end. Ramped, so the cut does not jolt. */
  skip(rate = 8) {
    this.targetRate = rate;
    return this;
  }

  /** True once skipping is under way — used to thin out sound and sparks. */
  get skipping() {
    return this.targetRate > 1.5;
  }

  track(target, path, keys, easing) {
    if (target) this.tracks.push(new Track(target, path, keys, easing));
    return this;
  }

  at(t, fn) {
    this.events.push({ t, fn, fired: false });
    return this;
  }

  update(dt) {
    // Ease toward the target rate over ~0.15s so a skip accelerates rather
    // than snapping.
    if (this.rate !== this.targetRate) {
      const step = (this.targetRate - 1) * (dt / 0.15);
      this.rate = this.targetRate > this.rate
        ? Math.min(this.targetRate, this.rate + step)
        : Math.max(this.targetRate, this.rate + step);
    }
    this.time += dt * this.rate;
    const t = Math.min(this.time, this.duration);
    for (const track of this.tracks) track.sample(t);
    for (const evt of this.events) {
      if (!evt.fired && this.time >= evt.t) {
        evt.fired = true;
        evt.fn();
      }
    }
    if (this.time >= this.duration) this.done = true;
  }
}

/* ------------------------------------------------------------------ */
/* Particles                                                           */
/* ------------------------------------------------------------------ */

class Sparks {
  constructor(scene) {
    this.scene = scene;
    this.systems = [];
  }

  /** Emits a short-lived burst of points at `position`. */
  burst(position, { count = 40, color = 0xffcc55, speed = 3.2, life = 0.7, size = 0.06 } = {}) {
    const positions = new Float32Array(count * 3);
    const velocities = [];
    for (let i = 0; i < count; i++) {
      positions[i * 3] = position.x;
      positions[i * 3 + 1] = position.y;
      positions[i * 3 + 2] = position.z;
      const dir = new THREE.Vector3(
        Math.random() * 2 - 1,
        Math.random() * 1.4 + 0.2,
        Math.random() * 2 - 1
      ).normalize().multiplyScalar(speed * (0.4 + Math.random() * 0.6));
      velocities.push(dir);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const points = new THREE.Points(
      geometry,
      new THREE.PointsMaterial({
        color,
        size,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      })
    );
    this.scene.add(points);
    this.systems.push({ points, velocities, age: 0, life });
  }

  update(dt) {
    for (let i = this.systems.length - 1; i >= 0; i--) {
      const sys = this.systems[i];
      sys.age += dt;
      const attr = sys.points.geometry.getAttribute('position');
      for (let p = 0; p < sys.velocities.length; p++) {
        const v = sys.velocities[p];
        v.y -= 7 * dt; // gravity
        attr.setXYZ(
          p,
          attr.getX(p) + v.x * dt,
          Math.max(0.02, attr.getY(p) + v.y * dt),
          attr.getZ(p) + v.z * dt
        );
      }
      attr.needsUpdate = true;
      sys.points.material.opacity = Math.max(0, 1 - sys.age / sys.life);
      if (sys.age >= sys.life) {
        this.scene.remove(sys.points);
        sys.points.geometry.dispose();
        sys.points.material.dispose();
        this.systems.splice(i, 1);
      }
    }
  }

  /** Drops every live burst — abandoning a game mid-battle would leak them. */
  clear() {
    for (const sys of this.systems) {
      this.scene.remove(sys.points);
      sys.points.geometry.dispose();
      sys.points.material.dispose();
    }
    this.systems = [];
  }
}

/* ------------------------------------------------------------------ */
/* Choreography helpers                                                */
/* ------------------------------------------------------------------ */

/**
 * Collects keyframes per (object, property) and emits one track for each.
 *
 * A `Track` holds its property for the whole timeline — before its first key
 * it clamps to that key's value — so two tracks on the same property do not
 * blend. The later one simply wins, silently, and the earlier animation never
 * appears. With a handful of beats per fight that is easy to do by accident
 * and very hard to spot afterwards.
 *
 * Routing every write through one book makes it structural: contributions from
 * the approach, the choreography and the collapse merge into a single sorted
 * key list. It exposes `track` and `at` as well as `add`, so anything that
 * takes a Timeline can be handed a book instead and needs no changes.
 */
/**
 * Round shot in flight.
 *
 * A ball is one object travelling between two known points, where a spark
 * burst is many going nowhere in particular — so it gets its own small pool
 * rather than bending Sparks into a shape it was not built for. Like the
 * debris field, it outlives the keyframes that launched it and is ticked from
 * update(), because a timeline cannot hold a track for an object that does not
 * exist when the timeline is built.
 */
class Shots {
  constructor(scene) {
    this.scene = scene;
    this.live = [];
    // One geometry and one material for every ball ever fired. These belong to
    // this pool rather than the shared part library, so disposing them here is
    // safe — but there is still no reason to build a sphere per shot.
    this.geometry = new THREE.SphereGeometry(0.032, 10, 8);
    this.material = new THREE.MeshStandardMaterial({
      color: 0x24242a, roughness: 0.45, metalness: 0.75
    });
  }

  /** Launches a ball from `from`, arriving at `to` in `flight` seconds. */
  fire(from, to, flight = 0.24) {
    const mesh = new THREE.Mesh(this.geometry, this.material);
    mesh.position.copy(from);
    mesh.castShadow = true;
    this.scene.add(mesh);
    this.live.push({ mesh, from: from.clone(), to: to.clone(), flight, age: 0 });
  }

  update(dt) {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const shot = this.live[i];
      shot.age += dt;
      const n = Math.min(1, shot.age / shot.flight);
      shot.mesh.position.lerpVectors(shot.from, shot.to, n);
      // A shallow arc over the flight, so it reads as a thrown weight rather
      // than a beam. It is gone by the time it would have to fall properly.
      shot.mesh.position.y += Math.sin(n * Math.PI) * 0.10;
      if (n >= 1) {
        this.scene.remove(shot.mesh);
        this.live.splice(i, 1);
      }
    }
  }

  clear() {
    for (const shot of this.live) this.scene.remove(shot.mesh);
    this.live.length = 0;
  }
}

class TrackBook {
  constructor(timeline) {
    this.timeline = timeline;
    this.entries = new Map();
    this.ids = new WeakMap();
    this.nextId = 1;
  }

  keyFor(target, path) {
    let id = this.ids.get(target);
    if (id === undefined) {
      id = this.nextId++;
      this.ids.set(target, id);
    }
    return `${id}|${path}`;
  }

  add(target, path, keys, easing = 'easeInOut') {
    if (!target || !keys?.length) return this;
    const id = this.keyFor(target, path);
    const entry = this.entries.get(id);
    if (entry) entry.keys.push(...keys);
    else this.entries.set(id, { target, path, keys: [...keys], easing });
    return this;
  }

  track(target, path, keys, easing) {
    return this.add(target, path, keys, easing);
  }

  at(t, fn) {
    this.timeline.at(t, fn);
    return this;
  }

  /** Sorts, de-duplicates and emits. Call once, before playing. */
  flush() {
    for (const entry of this.entries.values()) {
      const sorted = [...entry.keys].sort((a, b) => a.t - b.t);
      const keys = [];
      for (const key of sorted) {
        // Two keys at the same instant would make the segment zero-length;
        // the last one wins, matching how a plain Track would behave.
        if (keys.length && keys[keys.length - 1].t === key.t) keys[keys.length - 1] = key;
        else keys.push(key);
      }
      this.timeline.track(entry.target, entry.path, keys, entry.easing);
    }
    this.entries.clear();
    return this.timeline;
  }
}

const parts = (piece) => piece.userData.parts ?? {};

const WORLD_UP = new THREE.Vector3(0, 1, 0);

/**
 * Builds the impulse for a part being knocked off.
 *
 * `away` points from the attacker toward the victim, so wreckage carries on in
 * the direction of the blow. `spread` throws it off that line, and the spin is
 * randomised so no two hits look alike.
 */
function shedImpulse(away, { power = 2, lift = 1.3, spread = 0.6, spin = 9 } = {}) {
  const side = new THREE.Vector3(-away.z, 0, away.x);
  const velocity = away.clone()
    .multiplyScalar(power * (0.8 + Math.random() * 0.4))
    .addScaledVector(WORLD_UP, lift * (0.7 + Math.random() * 0.6))
    .addScaledVector(side, (Math.random() * 2 - 1) * spread);
  const tumble = () => (Math.random() * 2 - 1) * spin;
  return { velocity, spin: new THREE.Vector3(tumble(), tumble(), tumble()) };
}

/**
 * Adds a two-beat walk cycle for the span [start, end].
 *
 * Takes a Timeline or a TrackBook. `moveTo` passes the timeline directly;
 * `battle` passes a book, because there the bob competes with the
 * choreography's own claim on `position.y`.
 */
function addWalkCycle(timeline, piece, start, end, strides = 3) {
  const p = parts(piece);
  const step = (end - start) / (strides * 2);
  const legL = [];
  const legR = [];
  const armL = [];
  const armR = [];
  for (let i = 0; i <= strides * 2; i++) {
    const t = start + step * i;
    const swing = i % 2 === 0 ? 0.55 : -0.55;
    legL.push({ t, value: swing });
    legR.push({ t, value: -swing });
    armL.push({ t, value: -swing * 0.7 });
    armR.push({ t, value: swing * 0.5 });
  }
  timeline
    .track(p.legL, 'rotation.x', legL, 'easeInOut')
    .track(p.legR, 'rotation.x', legR, 'easeInOut')
    .track(p.armL, 'rotation.x', armL, 'easeInOut')
    .track(p.armR, 'rotation.x', armR, 'easeInOut');

  // A subtle vertical bob sells the footfalls.
  const bob = [];
  for (let i = 0; i <= strides * 4; i++) {
    bob.push({ t: start + ((end - start) / (strides * 4)) * i, value: i % 2 === 0 ? 0 : 0.045 });
  }
  timeline.track(piece.position, 'y', bob, 'easeInOut');
}


/**
 * Adds a horse gait for the span [start, end].
 *
 * Takes a Timeline or a TrackBook, like addWalkCycle above: `moveTo` passes
 * its timeline directly, the battle variants pass a book. Both answer to
 * `track`, which is why it is used here rather than the book's own `add`.
 *
 * A horse is not a biped with four legs. Its footfalls are ordered, and the
 * order is what tells a walk from a canter from a gallop at a glance:
 *
 *   walk    four beats, one hoof at a time, diagonal pairs a quarter apart
 *   canter  three beats, the diagonal pair landing together
 *   gallop  four beats gathered into two, with a moment of suspension
 *
 * Phases below are fractions of a stride, in the order the hooves land. The
 * legs are single segments from shoulder to hoof, so this is a rotation about
 * the shoulder and hip — enough to read as a gait, which a rigid four-legged
 * prop sliding across the board never does.
 */
const GAITS = {
  walk:   { phase: { legBL: 0.00, legFL: 0.25, legBR: 0.50, legFR: 0.75 },
            swing: 0.42, bob: 0.012, lean: 0.03 },
  canter: { phase: { legBL: 0.00, legFL: 0.35, legBR: 0.35, legFR: 0.70 },
            swing: 0.58, bob: 0.030, lean: 0.08 },
  gallop: { phase: { legBL: 0.00, legBR: 0.14, legFL: 0.48, legFR: 0.62 },
            swing: 0.72, bob: 0.045, lean: 0.14 }
};

function addHorseGait(timeline, piece, start, end,
                      { kind = 'walk', strides = 3, bob = false } = {}) {
  const p = parts(piece);
  if (!p.legFL) return false;          // a primitive knight has no horse joints
  const gait = GAITS[kind] ?? GAITS.walk;
  const span = end - start;
  if (span <= 0) return false;
  const stride = span / strides;
  // Sampled rather than keyed at the extremes: the phase offsets are the whole
  // point, and four legs keyed on their own turning points would each need a
  // different key list anyway.
  const steps = 8;

  for (const [leg, phase] of Object.entries(gait.phase)) {
    const joint = p[leg];
    if (!joint) continue;
    const keys = [];
    for (let i = 0; i <= strides * steps; i++) {
      const t = start + (span / (strides * steps)) * i;
      const cycle = (i / steps + phase) % 1;
      keys.push({ t, value: Math.sin(cycle * Math.PI * 2) * gait.swing });
    }
    timeline.track(joint, 'rotation.x', keys, 'linear');
  }

  // The body rides the footfalls: two dips per stride, and the head and tail
  // answer the body a beat late, which is most of what sells it as an animal.
  const bobKeys = [];
  const neck = [];
  const tail = [];
  for (let i = 0; i <= strides * 4; i++) {
    const t = start + (span / (strides * 4)) * i;
    const wave = Math.sin((i / 2) * Math.PI);
    bobKeys.push({ t, value: Math.abs(wave) * gait.bob });
    neck.push({ t, value: -wave * gait.lean });
    tail.push({ t, value: wave * gait.lean * 1.6 });
  }
  // The bob is opt-in because it writes piece.position.y, and every place that
  // moves a knight — the vault, the charge, the run-down — already owns that
  // channel. Two claims on it would merge into nonsense rather than collide
  // loudly, which is the failure mode TrackBook exists to prevent.
  if (bob) timeline.track(piece.position, 'y', bobKeys, 'easeInOut');
  if (p.neck) timeline.track(p.neck, 'rotation.x', neck, 'easeInOut');
  if (p.horseHead) timeline.track(p.horseHead, 'rotation.x',
    neck.map((k) => ({ t: k.t + stride * 0.08, value: -k.value * 0.7 })), 'easeInOut');
  if (p.tail) timeline.track(p.tail, 'rotation.x', tail, 'easeInOut');
  return true;
}

/**
 * The ordinary gait `moveTo()` already gives every piece, applied to a
 * fight's approach and return instead — a knight vaults, a bishop or queen
 * glides. Before this, only pawns and kings got a walk cycle in `battle()`
 * and everything else slid flat, which is the "not graceful" a knight or
 * queen fight looked like: the same piece that hops or hovers on an ordinary
 * move suddenly turned into a ghost the moment its move was a capture.
 *
 * The rook is deliberately not here: its lift during a fight is shallower
 * than the hover `moveTo()` gives it on an ordinary move (a fight is a lean
 * in, not a full glide across the board), so `battle()` keeps that lift as
 * its own inline case, unchanged. Pawns and kings likewise stay as a direct
 * `addWalkCycle` call at the site that needs it — both already matched what
 * this would produce, so there was nothing there to fix.
 */
function addLocomotion(book, piece, start, end, distance, { skipHover = false } = {}) {
  const p = parts(piece);
  const type = piece.userData.type;
  const span = end - start;
  if (span <= 0) return;

  if (type === 'n') {
    book.track(piece.position, 'y', [
      { t: start, value: 0 },
      { t: start + span * 0.5, value: 1.1 },
      { t: end, value: 0 }
    ], 'easeInOut');
    book.track(p.mount ?? piece, 'rotation.x', [
      { t: start, value: 0 },
      { t: start + span * 0.45, value: -0.35 },
      { t: start + span * 0.85, value: 0.2 },
      { t: end, value: 0 }
    ], 'easeInOut');
    // A short hop across one square still reads as a stride; a long slide
    // to a standoff several squares off gets more of them, rather than one
    // stretched leap that plays like slow motion.
    const strides = Math.max(1, Math.round(distance / 0.7));
    addHorseGait(book, piece, start, end, { kind: 'gallop', strides });
  } else if (type === 'b' || type === 'q') {
    // Skipped for a variant that already levitates for the whole fight
    // (`spec.hover` — every queen manner, and the bishop's Excommunication):
    // its own choreography already keeps the piece off the ground well past
    // `end`, and a second, shorter hover on the same property here would
    // fight it rather than add to it.
    if (!skipHover) {
      book.track(piece.position, 'y', [
        { t: start, value: 0 },
        { t: start + span * 0.5, value: 0.28 },
        { t: end, value: 0 }
      ], 'easeInOut');
    }
    // A trailing arm sells the drift, for the casters that have one.
    book.track(p.armR, 'rotation.x', [
      { t: start, value: 0 },
      { t: start + span * 0.5, value: -0.5 },
      { t: end, value: 0 }
    ], 'easeInOut');
  }
}

/**
 * Puts back anything a fight broke: unhides the intact parts and removes the
 * damaged stand-ins. Victims die, so this only really matters for a piece that
 * survives — but a permanently one-armed pawn is exactly the sort of bug that
 * ships, so it is done unconditionally.
 */
function restoreDamage(piece) {
  const record = piece.userData.damaged;
  if (!record?.length) return;
  const held = piece.userData.parts ?? {};
  for (const { hidden, added, slot } of record) {
    if (hidden) hidden.visible = true;
    added?.parent?.remove(added);
    // Putting the mesh back is not enough: shedSlot also cleared the handle,
    // and without this the piece keeps the part but can never use it again.
    if (slot && hidden) {
      if (slot.index === undefined) held[slot.name] = hidden;
      else if (held[slot.name]) held[slot.name][slot.index] = hidden;
    }
  }
  piece.userData.damaged = null;
}

/** Resets an armature to its rest pose. */
export function resetPose(piece) {
  const p = parts(piece);
  restoreDamage(piece);
  // Zero is the usual rest, but not always: a piece may declare its own in
  // `userData.rest`. That is what the model editor writes so its adjustments
  // survive a move, and it is the general form of the knight's seated rider
  // below — a joint whose rest simply is not zero.
  const rest = piece.userData.rest ?? {};
  for (const key of ['legL', 'legR', 'armL', 'armR', 'torso', 'head', 'hips',
                     'neck', 'horseHead', 'tail',
                     'legFL', 'legFR', 'legBL', 'legBR']) {
    const joint = p[key];
    if (!joint) continue;
    const at = rest[key];
    joint.rotation.set(at?.[0] ?? 0, at?.[1] ?? 0, at?.[2] ?? 0);
  }
  if (p.mount) {
    const at = rest.mount;
    p.mount.rotation.set(at?.[0] ?? 0, at?.[1] ?? 0, at?.[2] ?? 0);
  }
  // Bishop only, and only when it has one: shutBook() closes the book's hinge
  // for the slap and nothing else reopens it, so without this every bishop
  // that ever wins a fight keeps its book shut for the rest of the game — a
  // real, shipped gap, not just how the editor looked after Play.
  if (p.book) setBookOpen(p.book, 1);
  // And the castle's gun goes back behind its port, for the same reason:
  // nothing else runs it back in, so without this it stays out for good.
  if (p.cannon) stowCannon(p.cannon);
  // A sculpted piece carries its rest pose in the geometry: its rider is
  // already seated, so zero is the correct rotation and re-applying the
  // primitive rig's hand-authored straddle would fold its legs twice.
  if (p.legL && piece.userData.type === 'n' && !piece.userData.rigged) {
    // The knight's rider stays seated, straddling the horse. Order 'ZXY'
    // applies the outward splay before the forward lean — see the matching
    // comment in pieces.js, which sets this same rest pose on spawn.
    p.legL.rotation.set(-0.62, 0, -0.30, 'ZXY');
    p.legR.rotation.set(-0.62, 0, 0.30, 'ZXY');
  }
  piece.position.y = 0;
  piece.rotation.set(0, 0, 0);
  piece.scale.setScalar(1);
  piece.userData.facing.rotation.y = piece.userData.color === 'w' ? 0 : Math.PI;
  piece.visible = true;
}

/* ------------------------------------------------------------------ */
/* Animator                                                            */
/* ------------------------------------------------------------------ */

export class Animator {
  constructor(scene, { onSound = () => {} } = {}) {
    this.scene = scene;
    this.sparks = new Sparks(scene);
    this.shots = new Shots(scene);
    // Wreckage outlives the timeline that produced it, so it is owned here
    // and ticked from update() rather than driven by keyframes.
    this.debris = new DebrisField(scene, {
      onImpact: (material, force, position) => {
        const { cue, pitch, gain } = resolveLanding(material);
        this.onSound(cue, {
          impact: true, pitch, pan: panAt(position),
          gain: gain * Math.min(1.1, 0.3 + force * 0.4)
        });
      }
    });
    this.queue = [];
    this.current = null;
    this.skipAll = false;
    this.emitSound = onSound;
  }

  get busy() {
    return !!this.current || this.queue.length > 0;
  }

  /**
   * Sound gate. While a timeline is being skipped its blows land a few
   * milliseconds apart, so playing every cue turns a battle into a burst of
   * noise; only impacts loud enough to matter get through.
   */
  onSound(name, opts) {
    if (this.current?.skipping && !LOUD_CUES.has(name)) return;
    this.emitSound(name, opts);
  }

  /**
   * A blow landing. The sound is chosen by what struck what — a spear on a
   * steel helm rings, the same spear on masonry only scrapes — then shifted
   * into the attacker's own timbre so each piece stays recognisable.
   */
  onBlow(attacker, manner, surface, position, { force = 1 } = {}) {
    const board = PIECE_SOUND[attacker.userData.type] ?? PIECE_SOUND.p;
    const { cue, pitch, gain } = resolveImpact(IMPACTOR_BY_MANNER[manner] ?? 'steel', surface);
    this.onSound(cue, {
      impact: true,
      pitch: pitch * board.pitch,
      gain: gain * board.gain * Math.min(1.2, force),
      pan: panAt(position)
    });
  }

  /**
   * A creature's own voice — a horse's snort, a golem's groan. Fired at most
   * once per fight by default: a whinny on every capture becomes a joke by
   * the fourth. `repeat: true` (only ever passed for `hurt`) skips that —
   * a hurt reaction on every beat, not just the finisher, is the point.
   *
   * `sampleEvent` is only set for the two keys with a drop-in pool
   * (public/audio/sfx/{attack,hurt}/) — every other voice (charge, cast,
   * effort, fall, death) has no pool and keeps playing its synthesized cue
   * exactly as before, unaffected by this parameter's default.
   */
  onVoice(piece, key, position, sampleEvent = null, { repeat = false } = {}) {
    const cue = (PIECE_SOUND[piece.userData.type] ?? {})[key];
    if (!cue) return;
    if (!repeat) {
      if (this.voiced?.has(cue)) return;
      (this.voiced ??= new Set()).add(cue);
    }
    const opts = { gain: 0.8, pan: position ? panAt(position) : 0 };
    if (sampleEvent) {
      opts.sample = sampleEvent;
      opts.pool = POOL_BY_TYPE[piece.userData.type] ?? 'male';
    }
    this.onSound(cue, opts);
  }

  /**
   * Fast-forwards whatever is playing. Everything still runs — every event
   * fires in order and anything already shed keeps falling — it just runs out
   * in about a second.
   */
  skip() {
    this.skipAll = true;
    this.current?.skip();
  }

  play(timeline) {
    return new Promise((resolve) => {
      timeline.resolve = resolve;
      this.queue.push(timeline);
    });
  }

  update(dt) {
    this.sparks.update(dt);
    this.shots.update(dt);
    this.debris.update(dt);
    if (!this.current) {
      this.current = this.queue.shift() ?? null;
      // A skip covers everything still queued behind it, not just the
      // timeline that happened to be playing when the player asked.
      if (this.current && this.skipAll) this.current.skip();
      if (!this.current) this.skipAll = false;
    }
    if (!this.current) return;
    this.current.update(dt);
    if (this.current.done) {
      const finished = this.current;
      this.current = null;
      finished.resolve?.();
    }
  }

  /** Cancels everything in flight — used when a game is abandoned. */
  clear() {
    for (const tl of [this.current, ...this.queue]) tl?.resolve?.();
    this.current = null;
    this.queue = [];
    this.skipAll = false;
    this.debris.clear();
    this.sparks.clear();
    this.shots.clear();
  }

  /* ---------------- movement ---------------- */

  /**
   * Walks a piece from one square to another. Knights vault over intervening
   * pieces, bishops and queens glide, and everyone else marches.
   */
  moveTo(piece, fromSquare, toSquare, { duration } = {}) {
    const from = squareToWorld(fromSquare);
    const to = squareToWorld(toSquare);
    const distance = from.distanceTo(to);
    const type = piece.userData.type;
    // The rook glides too: a castle does not walk, it lifts clear of the board
    // and hovers across it.
    const glides = type === 'b' || type === 'q' || type === 'r';
    const vaults = type === 'n';
    const time = duration ?? Math.min(1.5, 0.42 + distance * 0.13);

    const tl = new Timeline(time);
    const p = parts(piece);

    // Turn to face the direction of travel before setting off.
    const heading = Math.atan2(to.x - from.x, to.z - from.z) + Math.PI;
    const baseFacing = piece.userData.color === 'w' ? 0 : Math.PI;
    const turn = 0.18;
    tl.track(piece.userData.facing, 'rotation.y', [
      { t: 0, value: baseFacing },
      { t: turn, value: heading },
      { t: time - turn, value: heading },
      { t: time, value: baseFacing }
    ], 'easeInOut');

    tl.track(piece.position, 'x', [
      { t: turn, value: from.x },
      { t: time - turn * 0.5, value: to.x }
    ], 'easeInOut');
    tl.track(piece.position, 'z', [
      { t: turn, value: from.z },
      { t: time - turn * 0.5, value: to.z }
    ], 'easeInOut');

    if (vaults) {
      tl.track(piece.position, 'y', [
        { t: turn, value: 0 },
        { t: time * 0.5, value: 1.1 },
        { t: time - turn * 0.5, value: 0 }
      ], 'easeInOut');
      tl.track(p.mount ?? piece, 'rotation.x', [
        { t: turn, value: 0 },
        { t: time * 0.5, value: -0.35 },
        { t: time - turn * 0.5, value: 0.2 },
        { t: time, value: 0 }
      ], 'easeInOut');
      // Legs gather, tuck and reach across the leap. Without this the horse is
      // a rigid prop flying through the air, which is what it was before.
      addHorseGait(tl, piece, turn, time - turn * 0.5,
        { kind: 'gallop', strides: 2 });
      tl.at(turn, () => this.onSound(PIECE_SOUND[piece.userData.type]?.move ?? 'gallop'));
    } else if (glides) {
      tl.track(piece.position, 'y', [
        { t: turn, value: 0 },
        { t: time * 0.5, value: 0.28 },
        { t: time - turn * 0.5, value: 0 }
      ], 'easeInOut');
      // A trailing arm sells the drift, for the casters that have one. The
      // castle does not, and track() drops it.
      tl.track(p.armR, 'rotation.x', [
        { t: turn, value: 0 },
        { t: time * 0.5, value: -0.5 },
        { t: time, value: 0 }
      ], 'easeInOut');
      tl.at(turn, () => this.onSound(PIECE_SOUND[piece.userData.type]?.move ?? 'glide'));
    } else {
      addWalkCycle(tl, piece, turn, time - turn * 0.5, Math.max(2, Math.round(distance)));
      tl.at(turn, () => this.onSound(PIECE_SOUND[piece.userData.type]?.move ?? 'stepLeather'));
    }

    return this.play(tl);
  }

  /** Slides a piece with no fanfare — used for the rook half of a castle. */
  slideTo(piece, fromSquare, toSquare, duration = 0.5) {
    const from = squareToWorld(fromSquare);
    const to = squareToWorld(toSquare);
    const tl = new Timeline(duration);
    tl.track(piece.position, 'x', [{ t: 0, value: from.x }, { t: duration, value: to.x }], 'easeInOut');
    tl.track(piece.position, 'z', [{ t: 0, value: from.z }, { t: duration, value: to.z }], 'easeInOut');
    return this.play(tl);
  }

  /* ---------------- battles ---------------- */

  /**
   * Plays the capture sequence: the attacker closes on the victim's square,
   * delivers a type-specific finishing move, and the victim is dispatched.
   * Resolves once the attacker is standing on the captured square.
   *
   * `fightSquare` is where the victim actually is and everything about the
   * fight itself — heading, approach, every impact — is measured against it.
   * `toSquare` is only where the attacker ends up once it's over. These are
   * the same square for every ordinary capture, which is why `fightSquare`
   * defaults to `toSquare` and every existing caller needs no change. They
   * differ for en passant: `toSquare` is the empty diagonal the pawn lands
   * on, and the victim sits one file over, on `fightSquare`. Calling this
   * with only `toSquare` for that capture turned "face to face" into two
   * pawns squared off against an empty square instead of each other.
   *
   * `onlookers`, if given, is every other piece on the board: each one turns
   * its head toward the fight for the fight's duration and back afterward.
   */
  battle(attacker, victim, fromSquare, toSquare, fightSquare = toSquare, { onlookers } = {}) {
    const from = squareToWorld(fromSquare);
    const fight = squareToWorld(fightSquare);
    const to = squareToWorld(toSquare);
    const heading = Math.atan2(fight.x - from.x, fight.z - from.z) + Math.PI;

    // Stop a fixed distance short of the victim — the square next to it —
    // rather than a fraction of however far this particular move travels.
    // A fraction only ever looked right by coincidence: it left an adjacent
    // capture barely moved at all, while a piece attacking from several
    // squares away stayed planted near its own start, nowhere near "next to"
    // anything. `spec.standoff` is that fixed distance, in board units;
    // clamped so a very short move (an adjacent capture) never pushes the
    // attacker past its own square trying to reach it. How close depends on
    // the manner: a melee attacker needs to stay near enough that its weapon
    // still reads as connecting; a castle or a spell only needs line of sight.
    const dist = from.distanceTo(fight);
    const spec = buildSpec(attacker, victim, dist);
    const gap = Math.min(spec.standoff, dist);
    const approach = dist > 1e-6
      ? fight.clone().add(from.clone().sub(fight).normalize().multiplyScalar(gap))
      : fight.clone();
    const total = spec.duration;
    const strike = spec.finisher.t;

    const tl = new Timeline(total);
    // Which set piece is playing, for debugging and for tests.
    tl.variant = spec.variant;
    // Creature voices are limited to one apiece per fight.
    this.voiced = new Set();
    tl.at(Math.max(0.2, spec.approachEnd - 0.3), () => {
      this.onVoice(attacker, 'charge', approach);
      this.onVoice(attacker, 'cast', approach);
      this.onVoice(attacker, 'attack', approach, 'attack');
    });
    // Every contribution below — the approach, the choreography, each beat's
    // reel and the collapse — writes through one book, so overlapping claims
    // on the same property merge instead of overwriting one another.
    const book = new TrackBook(tl);
    const ap = parts(attacker);
    const vp = parts(victim);

    book.add(attacker.userData.facing, 'rotation.y', [
      { t: 0, value: attacker.userData.color === 'w' ? 0 : Math.PI },
      { t: 0.2, value: heading }
    ], 'easeInOut');
    book.add(victim.userData.facing, 'rotation.y', [
      { t: 0, value: victim.userData.color === 'w' ? 0 : Math.PI },
      { t: 0.25, value: heading + Math.PI }
    ], 'easeInOut');

    // Close the distance.
    const closeEnd = spec.approachEnd;
    book.add(attacker.position, 'x', [
      { t: 0.15, value: from.x }, { t: closeEnd, value: approach.x }
    ], 'easeInOut');
    book.add(attacker.position, 'z', [
      { t: 0.15, value: from.z }, { t: closeEnd, value: approach.z }
    ], 'easeInOut');
    // The rook is not here: a castle hovers in rather than marching.
    if (attacker.userData.type === 'p' || attacker.userData.type === 'k') {
      addWalkCycle(book, attacker, 0.15, closeEnd, 2);
    } else if (attacker.userData.type === 'r') {
      book.add(attacker.position, 'y', [
        { t: 0.15, value: 0 }, { t: closeEnd * 0.5, value: 0.10 }, { t: closeEnd, value: 0 }
      ], 'easeInOut');
    } else if (!spec.ownsApproach) {
      addLocomotion(book, attacker, 0.15, closeEnd, dist - gap, { skipHover: spec.hover });
    }

    spec.build(book, {
      attacker, victim, ap, vp, from, to: fight, approach,
      beats: spec.beats, finisher: spec.finisher, total,
      // For set pieces with effects of their own to launch — the castle's
      // shot is spawned at fire time, not keyframed.
      animator: this
    });

    // Direction of the blow: wreckage carries on the way the attacker was going.
    const away = new THREE.Vector3().subVectors(fight, from).setY(0).normalize();

    // Each landed blow: its own sound, its own sparks at the point of contact,
    // one piece of the victim knocked loose, and its own hurt reaction — not
    // deduped to once per fight the way the other creature voices are, since
    // the whole point is one per beat.
    for (const beat of spec.beats) {
      tl.at(beat.t, () => {
        const at = this.impactPoint(victim, beat.impact, fight);
        // An opener takes nothing, so there is no surface to strike: the
        // parry rings off the victim's own steel instead.
        this.onBlow(attacker, spec.manner, beat.slot?.material ?? 'steel', at);
        this.sparks.burst(at, beat.spark ?? {});
        this.shedSlot(victim, beat.slot, away);
        this.onVoice(victim, 'hurt', at, 'hurt', { repeat: true });
        // The one case where the attacker loses something: a lance shattering
        // against stone. It survives the fight, so this must be restorable —
        // shedSlot refuses anything that could not be put back.
        if (beat.attackerShed) {
          this.shedSlot(attacker, beat.attackerShed, away.clone().negate(), { restorable: true });
        }
      });
      addReaction(book, victim, vp, beat, away, fight);
    }

    tl.at(strike, () => {
      const at = this.impactPoint(victim, spec.finisher.impact, fight);
      this.onBlow(attacker, spec.manner, spec.finisher.material ?? 'steel', at, { force: 1.35 });
      this.sparks.burst(at, spec.finisher.spark ?? {});
      // The attacker's effort, and the victim's end.
      this.onVoice(attacker, 'effort', at);
      this.onVoice(victim, 'death', at);
      this.onVoice(victim, 'fall', at);
      this.onVoice(victim, 'hurt', at, 'hurt', { repeat: true });
    });

    this.addDefeat(book, victim, strike, total, spec.defeat);

    // The victor advances onto the square it just cleared — `to`, the actual
    // landing square, not `fight`: for an ordinary capture the two coincide,
    // but en passant's landing square is the empty diagonal, not the file the
    // victim was cleared from.
    book.add(attacker.position, 'x', [
      { t: total - 0.55, value: approach.x }, { t: total - 0.1, value: to.x }
    ], 'easeInOut');
    book.add(attacker.position, 'z', [
      { t: total - 0.55, value: approach.z }, { t: total - 0.1, value: to.z }
    ], 'easeInOut');
    book.add(attacker.userData.facing, 'rotation.y', [
      { t: total - 0.4, value: heading },
      { t: total, value: attacker.userData.color === 'w' ? 0 : Math.PI }
    ], 'easeInOut');
    // The castle lifts again to take the square it has just cleared, rather
    // than sliding onto it along the ground.
    if (attacker.userData.type === 'r') {
      book.add(attacker.position, 'y', [
        { t: total - 0.55, value: 0 },
        { t: total - 0.32, value: 0.10 },
        { t: total - 0.05, value: 0 }
      ], 'easeInOut');
    } else if (attacker.userData.type !== 'p' && attacker.userData.type !== 'k') {
      addLocomotion(book, attacker, total - 0.55, total - 0.1, approach.distanceTo(to), { skipHover: spec.hover });
    }

    // Every piece not in this fight turns to watch it, and looks away again
    // once it's over — the same turn-and-settle timing the combatants'
    // facing already uses. Capped to a natural neck twist (about 70° either
    // way) so a piece with its back to the action cranes as far as it
    // comfortably can rather than snapping through its own shoulder.
    const HEAD_TURN_LIMIT = Math.PI * (70 / 180);
    const hold = Math.min(0.35, total / 2 - 0.01);
    for (const onlooker of onlookers ?? []) {
      if (onlooker === attacker || onlooker === victim) continue;
      const head = parts(onlooker).head;
      const square = onlooker.userData.square;
      if (!head || square == null) continue;
      const pos = squareToWorld(square);
      const bearing = Math.atan2(fight.x - pos.x, fight.z - pos.z) + Math.PI;
      // The head sits inside the same facing/orient chain the body's own
      // heading already accounts for, so the local turn it needs is the
      // world bearing minus whatever direction the piece already faces —
      // not the bearing itself, which would double-count that rotation.
      let yaw = bearing - onlooker.userData.facing.rotation.y;
      yaw = Math.atan2(Math.sin(yaw), Math.cos(yaw));
      yaw = Math.max(-HEAD_TURN_LIMIT, Math.min(HEAD_TURN_LIMIT, yaw));
      book.add(head, 'rotation.y', [
        { t: 0, value: 0 },
        { t: hold, value: yaw },
        { t: total - hold, value: yaw },
        { t: total, value: 0 }
      ], 'easeInOut');
    }

    book.flush();
    return this.play(tl);
  }

  /**
   * Where a blow lands, in world space. Sparks at the actual point of contact
   * rather than a fixed height matter a great deal over a ten-second fight.
   */
  impactPoint(victim, slot, fallback) {
    const node = parts(victim)[slot === 'head' ? 'head' : slot === 'chest' ? 'torso' : slot];
    if (node?.getWorldPosition) return node.getWorldPosition(new THREE.Vector3());
    return fallback.clone().setY(0.55);
  }

  /**
   * Knocks the part named by a damage slot loose, if the victim still has it.
   *
   * Where the manner of damage breaks things, the intact part is swapped for
   * its damaged state first — so a cleaved shield leaves in two halves and a
   * battlement spalls into chunks, leaving a sheared stump behind, rather than
   * a whole part simply falling off.
   */
  shedSlot(piece, slot, away, { restorable = false } = {}) {
    if (!slot) return null;
    const node = resolveSlot(piece, slot);
    if (!node) return null;

    const broken = slot.breakInto ? damagePart(node, slot.breakInto) : null;

    // A piece that survives the fight may only shed through a break. A break
    // hides the original and sheds stand-ins, so the original can be put back;
    // shedding the part itself hands the real object to the debris field and
    // nothing can restore it — which would leave a knight weaponless for the
    // rest of the game.
    if (restorable && !broken) return null;

    const targets = broken ? broken.shed : [node];
    if (broken) {
      const record = piece.userData.damaged ?? (piece.userData.damaged = []);
      record.push({
        hidden: broken.hidden,
        added: broken.stays,
        // Kept so restoreDamage can put the handle back, not just the mesh.
        slot: { name: slot.name, index: slot.index }
      });
    }

    let last = null;
    for (const target of targets) {
      const { velocity, spin } = shedImpulse(away, slot.fly);
      last = this.debris.shed(target, {
        ...slot.fly, kind: slot.kind, material: slot.material,
        permanent: slot.permanent, velocity, spin
      });
    }

    const held = piece.userData.parts;
    if (slot.index === undefined) held[slot.name] = null;
    else if (held[slot.name]) held[slot.name][slot.index] = null;
    return last;
  }

  /**
   * Knocks a named part off a piece and turns it loose.
   *
   * Returns null when the piece has no such part, so a damage table can name
   * armour that only some victims carry without guarding every call.
   */
  shedPart(piece, name, away, opts = {}) {
    const node = parts(piece)[name];
    if (!node) return null;
    const { velocity, spin } = shedImpulse(away, opts);
    // Spread first: `opts.spin` is the scalar magnitude shedImpulse consumed,
    // and must not shadow the vector it produced.
    const body = this.debris.shed(node, { ...opts, velocity, spin });
    // The joint keeps a stale reference otherwise, and resetPose would try to
    // pose something that is now lying on the board.
    if (body) parts(piece)[name] = null;
    return body;
  }

  /**
   * The victim's collapse: stagger, topple, sink and fade.
   *
   * Takes a TrackBook rather than the timeline so its torso and head keys
   * merge with anything the choreography already wrote for those joints,
   * instead of quietly replacing it.
   */
  addDefeat(tl, victim, strike, total, style = 'topple') {
    const vp = parts(victim);
    const end = total - 0.15;

    tl.track(vp.torso, 'rotation.x', [
      { t: strike, value: 0 },
      { t: strike + 0.12, value: -0.45 },
      { t: strike + 0.4, value: 0.3 }
    ], 'easeOut');
    tl.track(vp.head, 'rotation.x', [
      { t: strike, value: 0 },
      { t: strike + 0.15, value: -0.6 }
    ], 'easeOut');
    // Arms fly up as it goes down. The castle has none, and needs no guard
    // here: track() ignores a target that is not there.
    tl.track(vp.armL, 'rotation.x', [
      { t: strike, value: 0 },
      { t: strike + 0.2, value: -1.4 }
    ], 'easeOut');
    tl.track(vp.armR, 'rotation.x', [
      { t: strike, value: 0 },
      { t: strike + 0.2, value: -1.2 }
    ], 'easeOut');

    if (style === 'shatter') {
      // Stone opponents crumble straight down rather than falling over.
      tl.track(victim.scale, 'y', [
        { t: strike + 0.15, value: 1 }, { t: end, value: 0.05 }
      ], 'easeIn');
      tl.track(victim.scale, 'x', [
        { t: strike + 0.15, value: 1 }, { t: end, value: 1.25 }
      ], 'easeIn');
      tl.track(victim.scale, 'z', [
        { t: strike + 0.15, value: 1 }, { t: end, value: 1.25 }
      ], 'easeIn');
    } else if (style === 'vanish') {
      tl.track(victim.scale, 'x', [{ t: strike + 0.2, value: 1 }, { t: end, value: 0.01 }], 'easeIn');
      tl.track(victim.scale, 'y', [{ t: strike + 0.2, value: 1 }, { t: end, value: 0.01 }], 'easeIn');
      tl.track(victim.scale, 'z', [{ t: strike + 0.2, value: 1 }, { t: end, value: 0.01 }], 'easeIn');
      tl.track(victim, 'rotation.y', [
        { t: strike + 0.2, value: 0 }, { t: end, value: Math.PI * 4 }
      ], 'easeIn');
    } else if (style === 'kneel') {
      // Folds at the knees first, then goes over — the right ending for a
      // long fight, where an instant topple reads far too abruptly.
      if (vp.hips) tl.track(vp.hips, 'rotation.x', [
        { t: strike, value: 0 }, { t: strike + 0.45, value: 0.8 }
      ], 'easeOut');
      tl.track(victim, 'rotation.x', [
        { t: strike + 0.5, value: 0 }, { t: strike + 1.0, value: -1.2 }
      ], 'easeIn');
      tl.track(victim.position, 'y', [
        { t: strike + 0.3, value: 0 }, { t: strike + 0.9, value: -0.2 }, { t: end, value: -1.2 }
      ], 'easeIn');
    } else {
      tl.track(victim, 'rotation.x', [
        { t: strike + 0.25, value: 0 }, { t: strike + 0.75, value: -1.35 }
      ], 'easeIn');
      tl.track(victim.position, 'y', [
        { t: strike + 0.7, value: 0 }, { t: end, value: -1.2 }
      ], 'easeIn');
    }

    tl.at(end, () => { victim.visible = false; });
  }
}

/* ------------------------------------------------------------------ */
/* Per-attacker battle choreography                                    */
/* ------------------------------------------------------------------ */

// Never the same set piece twice running for a given attacker, so repeat
// captures by the same kind of piece stay varied.
const LAST_VARIANT = new Map();

function pickVariant(attacker, victim) {
  const type = attacker.userData.type;
  const set = BATTLES[type] ?? BATTLES.p;
  const eligible = set.filter((v) => !v.requires || v.requires(attacker, victim));

  // A named matchup is *the* story for its pairing, not a one-in-three
  // chance, so it takes precedence and sidesteps the no-repeat memory.
  const named = eligible.find((v) => v.exclusive);
  if (named) return named;

  const pool = eligible.length ? eligible : set;
  const last = LAST_VARIANT.get(type);
  const choices = pool.length > 1 ? pool.filter((v) => v !== last) : pool;
  const chosen = choices[(Math.random() * choices.length) | 0];
  LAST_VARIANT.set(type, chosen);
  return chosen;
}

/**
 * Builds the full set piece for one matchup.
 *
 * The damage table supplies what comes off and how it flies; the variant
 * supplies the attacker's own motion and the order of the victim's reactions.
 * Duration follows from how much the victim has to lose, which is what puts a
 * pawn skirmish near nine seconds and two golems grinding at each other near
 * twelve.
 */
function buildSpec(attacker, victim, distance = 0) {
  const variant = pickVariant(attacker, victim);
  const dmg = damageList(attacker, victim);
  const mismatch = isMismatched(attacker, victim);

  // A long slide needs more room to close than an adjacent capture, or it
  // reads as a fast, ungraceful dash covering several squares in the same
  // window as one. Only the lead-in stretches — every beat after it is timed
  // relative to `lead`, so the fight's own choreography is untouched.
  const lead = Math.max(variant.lead ?? 2.0, 0.6 + distance * 0.22);

  // An opener is a blow that lands but takes nothing — the king's parry, or a
  // lance shattering on stone. It *replaces* the first blow rather than being
  // added to them, so the fight opens differently without running longer.
  const opener = variant.opener ? [{ ...variant.opener, t: lead, slot: null }] : [];
  const offset = opener.length;
  const slots = offset
    ? dmg.slots.slice(0, Math.max(1, dmg.slots.length - offset))
    : dmg.slots;

  const beats = opener.concat(slots.map((slot, i) => ({
    t: lead + (i + offset) * dmg.beat,
    slot,
    sound: slot.sound,
    impact: slot.impact,
    // A spear ringing off a stone golem should look futile, not lethal.
    spark: mismatch
      ? { color: 0xb9b4a6, count: 12, speed: 2.2, life: 0.5 }
      : dmg.spark,
    reel: mismatch ? 'graze' : variant.reels[i % variant.reels.length]
  })));

  const finisherAt = lead + Math.max(1, beats.length) * dmg.beat + 0.9;
  return {
    variant: variant.name,
    // The manner names what the attacker strikes with, which the soundboard
    // crosses with whatever it lands on.
    manner: dmg.manner,
    // A caster whose own choreography already levitates for the whole fight
    // — see `queenTear`/`queenStorm`/`queenAscend`/`bishopExcommunication` —
    // owns `attacker.position.y` itself, and `battle()`'s own approach/return
    // glide has to stand down or the two fight over the same property.
    hover: !!variant.hover,
    // A knight variant that gallops in its own way from the first frame —
    // `knightCharge`'s back-off-then-pass, `knightRunDown`'s low trampling
    // run — owns the whole approach, position and gait alike; the generic
    // vault has nothing to add there and everything to collide with.
    ownsApproach: !!variant.ownsApproach,
    duration: finisherAt + 1.7,
    approachEnd: Math.max(0.9, lead - 0.5),
    defeat: dmg.collapse,
    // How far short of the victim the attacker stops, in board units — see
    // MANNER. The default covers every melee manner that hasn't set its own:
    // close enough for a swung weapon to still read as connecting, clear of
    // the two plinths actually touching (each is roughly 0.6-0.67 wide).
    standoff: dmg.standoff ?? 0.58,
    beats,
    finisher: {
      t: finisherAt,
      sound: variant.finishSound ?? 'hit',
      impact: 'chest',
      spark: { ...dmg.spark, count: (dmg.spark.count ?? 40) + 18 },
      // The killing blow lands on whatever is left, so it takes the last
      // slot's material rather than assuming armour.
      material: slots[slots.length - 1]?.material ?? 'steel',
      reel: 'fall'
    },
    build: variant.build
  };
}

/* ---------------- attacker motion ---------------- */

/** A thrust or swing of one arm, repeated once per beat, as a single track. */
function swing(book, joint, times, { wind = -0.5, hit = 1.3, recover = 0.1, lead = 0.45 } = {}) {
  if (!joint) return;
  const keys = [];
  for (const t of times) {
    keys.push(
      { t: t - lead, value: wind },
      { t: t - 0.08, value: hit },
      { t: t + 0.3, value: recover }
    );
  }
  book.add(joint, 'rotation.x', keys, 'easeOut');
}

/** The attacker leans into each blow and settles back between them. */
function lean(book, ap, times, amount = 0.28) {
  if (!ap.torso) return;
  const keys = [];
  for (const t of times) {
    keys.push({ t: t - 0.3, value: -amount * 0.5 }, { t: t, value: amount }, { t: t + 0.4, value: 0 });
  }
  book.add(ap.torso, 'rotation.x', keys, 'easeOut');
}


/**
 * How the victim takes a blow.
 *
 * Every template appends to the book rather than tracking directly, so three
 * beats produce one torso track with nine keys instead of three tracks in
 * which only the last would have survived. The victim never counters — these
 * reactions are the whole of what it can express.
 */
function addReaction(book, victim, vp, beat, away, home) {
  const t = beat.t;
  const side = new THREE.Vector3(-away.z, 0, away.x);
  // A golem aliases hips/legs onto its torso, so leg poses would fight the
  // torso's own keys for the same object.
  const hasLegs = vp.legL && vp.legL !== vp.torso;

  const knock = (distance, recover = 0.4) => {
    book.add(victim.position, 'x', [
      { t: t + 0.05, value: home.x },
      { t: t + 0.18, value: home.x + away.x * distance },
      { t: t + 0.6, value: home.x + away.x * distance * recover }
    ], 'easeOut');
    book.add(victim.position, 'z', [
      { t: t + 0.05, value: home.z },
      { t: t + 0.18, value: home.z + away.z * distance },
      { t: t + 0.6, value: home.z + away.z * distance * recover }
    ], 'easeOut');
  };

  switch (beat.reel) {
    case 'side': {
      const dir = Math.random() < 0.5 ? 1 : -1;
      book.add(vp.torso, 'rotation.z', [
        { t: t + 0.02, value: 0 }, { t: t + 0.14, value: 0.42 * dir }, { t: t + 0.6, value: 0.1 * dir }
      ], 'easeOut');
      book.add(vp.head, 'rotation.x', [
        { t: t + 0.02, value: 0 }, { t: t + 0.16, value: -0.5 }, { t: t + 0.6, value: -0.15 }
      ], 'easeOut');
      knock(0.07);
      break;
    }
    case 'spin':
      // Knocked half around, and left facing the wrong way.
      book.add(victim.userData.facing, 'rotation.y', [
        { t: t + 0.05, value: victim.userData.facing.rotation.y },
        { t: t + 0.45, value: victim.userData.facing.rotation.y + 1.5 }
      ], 'easeOut');
      book.add(vp.torso, 'rotation.x', [
        { t: t + 0.05, value: 0 }, { t: t + 0.2, value: -0.35 }, { t: t + 0.7, value: -0.1 }
      ], 'easeOut');
      knock(0.09);
      break;
    case 'stumble':
      book.add(vp.torso, 'rotation.x', [
        { t: t + 0.05, value: 0 }, { t: t + 0.2, value: -0.55 }, { t: t + 0.75, value: -0.2 }
      ], 'easeOut');
      if (hasLegs) {
        book.add(vp.legL, 'rotation.x', [
          { t: t + 0.1, value: 0 }, { t: t + 0.35, value: 0.7 }, { t: t + 0.8, value: 0.15 }
        ], 'easeOut');
        book.add(vp.legR, 'rotation.x', [
          { t: t + 0.1, value: 0 }, { t: t + 0.4, value: -0.5 }, { t: t + 0.85, value: 0 }
        ], 'easeOut');
      }
      knock(0.2, 0.7);
      break;
    case 'kneel':
      // Driven down, and it does not get back up.
      book.add(vp.hips, 'rotation.x', [{ t: t + 0.1, value: 0 }, { t: t + 0.7, value: 0.55 }], 'easeOut');
      if (hasLegs) {
        book.add(vp.legL, 'rotation.x', [{ t: t + 0.1, value: 0 }, { t: t + 0.7, value: 1.1 }], 'easeOut');
        book.add(vp.legR, 'rotation.x', [{ t: t + 0.15, value: 0 }, { t: t + 0.8, value: 0.9 }], 'easeOut');
      }
      book.add(victim.position, 'y', [{ t: t + 0.1, value: 0 }, { t: t + 0.7, value: -0.16 }], 'easeOut');
      break;
    case 'parry':
      // The victim gets its guard up and catches the blow. This is the only
      // defensive motion anywhere in the game, and it is meant to fail. A
      // castle has no guard to raise and simply takes it on the walls — add()
      // drops a target that is not there, so that needs no guard here.
      book.add(vp.armR, 'rotation.x', [
        { t: t - 0.55, value: 0 }, { t: t - 0.1, value: -1.9 }, { t: t + 0.7, value: -0.5 }
      ], 'easeOut');
      book.add(vp.armL, 'rotation.x', [
        { t: t - 0.45, value: 0 }, { t: t - 0.1, value: -1.4 }, { t: t + 0.7, value: -0.3 }
      ], 'easeOut');
      book.add(vp.torso, 'rotation.x', [
        { t: t - 0.2, value: 0 }, { t: t + 0.1, value: -0.22 }, { t: t + 0.7, value: 0 }
      ], 'easeOut');
      // Driven back a step by the force of it, but still standing.
      knock(0.13, 0.5);
      break;
    case 'graze':
      // A blow that barely registers — used where the matchup is hopeless.
      book.add(vp.torso, 'rotation.x', [
        { t: t + 0.05, value: 0 }, { t: t + 0.15, value: -0.08 }, { t: t + 0.45, value: 0 }
      ], 'easeOut');
      break;
    default:
      book.add(vp.torso, 'rotation.x', [
        { t: t + 0.05, value: 0 }, { t: t + 0.18, value: -0.5 }, { t: t + 0.6, value: -0.12 }
      ], 'easeOut');
      book.add(vp.head, 'rotation.x', [
        { t: t + 0.06, value: 0 }, { t: t + 0.2, value: -0.65 }, { t: t + 0.65, value: -0.2 }
      ], 'easeOut');
      knock(0.1);
      break;
  }
}

const beatTimes = (ctx) => [...ctx.beats.map((b) => b.t), ctx.finisher.t];

/* ---------------- pawn: spear ---------------- */

const pawnFlurry = {
  name: 'Thrust Flurry',
  lead: 2.0,
  reels: ['back', 'side', 'stumble'],
  finishSound: 'spear',
  build(book, ctx) {
    const times = beatTimes(ctx);
    swing(book, ctx.ap.armR, times, { wind: -0.6, hit: 1.45 });
    lean(book, ctx.ap, times, 0.3);
    // The shield arm comes up between thrusts.
    swing(book, ctx.ap.armL, times.map((t) => t + 0.25), { wind: -0.2, hit: -0.75, recover: -0.1, lead: 0.3 });
  }
};

const pawnShieldBash = {
  name: 'Shield Bind',
  lead: 2.2,
  reels: ['side', 'back', 'spin'],
  finishSound: 'spear',
  build(book, ctx) {
    const times = beatTimes(ctx);
    // Leads with the shield, follows with the spear.
    swing(book, ctx.ap.armL, times.map((t) => t - 0.18), { wind: -0.3, hit: -1.5, recover: -0.15, lead: 0.35 });
    swing(book, ctx.ap.armR, times, { wind: -0.75, hit: 1.3 });
    lean(book, ctx.ap, times, 0.36);
  }
};

const pawnLowSweep = {
  name: 'Low Sweep',
  lead: 1.9,
  reels: ['stumble', 'back', 'side'],
  finishSound: 'spear',
  build(book, ctx) {
    const times = beatTimes(ctx);
    swing(book, ctx.ap.armR, times, { wind: -0.25, hit: 0.95, lead: 0.5 });
    lean(book, ctx.ap, times, 0.45);
    // Drops into the thrust rather than standing tall.
    const dip = [];
    for (const t of times) dip.push({ t: t - 0.3, value: 0 }, { t, value: -0.07 }, { t: t + 0.35, value: 0 });
    book.add(ctx.attacker.position, 'y', dip, 'easeOut');
  }
};

/* ---------------- knight: mounted lance ---------------- */

const knightCharge = {
  name: 'Full Charge',
  lead: 2.3,
  reels: ['spin', 'stumble', 'back'],
  finishSound: 'lance',
  // Backs off and gallops in for each pass — its own choreography for the
  // whole approach, not a straight line toward a standoff point. `battle()`
  // has to leave the approach to it entirely rather than laying a generic
  // vault on top of a horse that is, at that same moment, riding backwards.
  ownsApproach: true,
  build(book, ctx) {
    const { attacker, ap, from, approach, to } = ctx;
    const times = beatTimes(ctx);
    const mount = ap.mount ?? attacker;
    // Backs off, then runs in for each pass.
    const rear = from.clone().lerp(approach, -0.25);
    const px = [{ t: 0.15, value: from.x }, { t: 0.6, value: rear.x }];
    const pz = [{ t: 0.15, value: from.z }, { t: 0.6, value: rear.z }];
    const hop = [{ t: 0.6, value: 0 }];
    const pitch = [{ t: 0.6, value: -0.5 }];
    for (const t of times) {
      px.push({ t: t - 1.0, value: rear.x }, { t, value: approach.x }, { t: t + 0.6, value: rear.x });
      pz.push({ t: t - 1.0, value: rear.z }, { t, value: approach.z }, { t: t + 0.6, value: rear.z });
      hop.push({ t: t - 0.6, value: 0.32 }, { t, value: 0 }, { t: t + 0.5, value: 0.18 });
      pitch.push({ t: t - 0.9, value: -0.55 }, { t, value: 0.18 }, { t: t + 0.5, value: -0.3 });
    }
    book.add(attacker.position, 'x', px, 'easeInOut');
    book.add(attacker.position, 'z', pz, 'easeInOut');
    book.add(attacker.position, 'y', hop, 'easeOut');
    book.add(mount, 'rotation.x', pitch, 'easeOut');
    // The lance couches for the pass.
    const couch = [];
    for (const t of times) couch.push({ t: t - 0.8, value: 0.5 }, { t: t - 0.1, value: -0.1 }, { t: t + 0.4, value: 0.3 });
    book.add(ap.armR, 'rotation.z', couch, 'easeOut');
    // A gallop under the passes. The bob stays off: `hop` above already owns
    // position.y, and two claims on it would merge rather than collide.
    addHorseGait(book, attacker, 0.6, times[times.length - 1] + 0.6,
      { kind: 'gallop', strides: Math.max(2, times.length * 2) });
  }
};

const knightWheel = {
  name: 'Wheel and Sabre',
  lead: 2.1,
  reels: ['side', 'spin', 'stumble'],
  finishSound: 'lance',
  // You do not wheel around a stone golem; the charge variant handles that.
  requires: (attacker, victim) => victim.userData.type !== 'r',
  build(book, ctx) {
    const { attacker, ap } = ctx;
    const times = beatTimes(ctx);
    const mount = ap.mount ?? attacker;
    // Circles the victim, striking down on each pass.
    const yaw = [{ t: 0.4, value: 0 }];
    times.forEach((t, i) => {
      yaw.push({ t: t - 0.5, value: (i + 1) * 0.9 }, { t: t + 0.4, value: (i + 1) * 1.5 });
    });
    book.add(attacker.userData.facing, 'rotation.y', yaw, 'easeInOut');
    swing(book, ap.armR, times, { wind: -1.5, hit: 0.9, lead: 0.5 });
    const bob = [];
    for (const t of times) bob.push({ t: t - 0.4, value: 0.16 }, { t, value: 0 }, { t: t + 0.4, value: 0.1 });
    book.add(attacker.position, 'y', bob, 'easeOut');
    book.add(mount, 'rotation.x', times.flatMap((t) => (
      [{ t: t - 0.4, value: -0.3 }, { t, value: 0.14 }]
    )), 'easeOut');
    // Circling, not charging: a canter reads as a horse being ridden round
    // something rather than one running at it.
    addHorseGait(book, attacker, 0.4, times[times.length - 1] + 0.4,
      { kind: 'canter', strides: Math.max(3, times.length * 2) });
  }
};

/* ---------------- the bishop: the book ---------------- */

/**
 * The hinge, at rest and shut.
 *
 * `setBookOpen` in pieces.js owns the same two numbers; a timeline drives the
 * hinge directly rather than calling through it, because a track needs a
 * property to write, so the two have to agree and are kept side by side.
 */
const BOOK_OPEN = Math.PI;
const BOOK_SHUT = 0;

/** Snaps the book shut by `at`, which is what turns a lectern into a weapon. */
function shutBook(book, ap, at) {
  const hinge = ap.book?.userData?.covers?.[1];
  if (!hinge) return;                 // the primitive bishop, which has none
  book.add(hinge, 'rotation.z', [
    { t: Math.max(0, at - 0.55), value: BOOK_OPEN },
    { t: at, value: BOOK_SHUT }
  ], 'easeIn');
}

/**
 * The swing itself: the arm crosses the body and comes back, so consecutive
 * blows land backhand, forehand, backhand rather than as one repeated motion.
 */
function backhand(book, ap, times, reach = 1.2) {
  if (!ap.armR) return;
  const keys = [];
  times.forEach((t, i) => {
    const dir = i % 2 ? 1 : -1;
    keys.push(
      { t: t - 0.45, value: dir * -reach * 0.9 },
      { t: t - 0.06, value: dir * reach },
      { t: t + 0.32, value: dir * reach * 0.2 }
    );
  });
  book.add(ap.armR, 'rotation.y', keys, 'easeOut');
}

const bishopChapterAndVerse = {
  name: 'Chapter and Verse',
  lead: 2.0,
  reels: ['side', 'spin', 'side'],
  build(book, ctx) {
    const { ap } = ctx;
    const times = beatTimes(ctx);
    shutBook(book, ap, times[0] - 0.9);
    backhand(book, ap, times, 1.25);
    swing(book, ap.armR, times, { wind: -0.7, hit: 0.45, lead: 0.4 });
    lean(book, ap, times, 0.2);
  }
};

const bishopExcommunication = {
  name: 'Excommunication',
  lead: 2.3,
  reels: ['back', 'kneel', 'stumble'],
  // Levitates for the whole fight, on `attacker.position.y` below — the one
  // bishop variant that owns that property itself. `battle()`'s own approach/
  // return glide has to stand down for it, or the two write conflicting
  // heights onto the same track.
  hover: true,
  build(book, ctx) {
    const { ap, attacker } = ctx;
    const times = beatTimes(ctx);
    // The book stays open for the reading, and shuts on the last word.
    shutBook(book, ap, times[times.length - 1] - 1.0);
    // A raised hand for the sentence, then the book comes down overhead.
    book.add(ap.armL, 'rotation.x', [
      { t: 0.4, value: 0 }, { t: times[0] - 0.4, value: -2.1 },
      { t: times[times.length - 1], value: -1.4 }, { t: ctx.total - 0.6, value: 0 }
    ], 'easeInOut');
    swing(book, ap.armR, times, { wind: -2.2, hit: 0.9, lead: 0.55 });
    lean(book, ap, times, 0.3);
    book.add(attacker.position, 'y', [
      { t: 0.3, value: 0 }, { t: 1.2, value: 0.18 },
      { t: ctx.total - 1.0, value: 0.18 }, { t: ctx.total - 0.2, value: 0 }
    ], 'easeInOut');
  }
};

const bishopCatechism = {
  name: 'Catechism',
  lead: 1.8,
  reels: ['side', 'side', 'spin'],
  // Slapping a stone golem with a book is a joke rather than a fight; the
  // longer, more ceremonial variant carries that better.
  requires: (attacker, victim) => victim.userData.type !== 'r',
  build(book, ctx) {
    const { ap, attacker } = ctx;
    const times = beatTimes(ctx);
    shutBook(book, ap, times[0] - 0.7);
    backhand(book, ap, times, 1.45);
    swing(book, ap.armR, times, { wind: -0.5, hit: 0.3, lead: 0.3 });
    // Steps in on each blow rather than standing and reaching.
    book.add(attacker.userData.facing, 'rotation.y',
      times.map((t, i) => ({ t: t + 0.2, value: (i % 2 ? 1 : -1) * 0.28 })), 'easeInOut');
    lean(book, ap, times, 0.16);
  }
};

/* ---------------- the queen: no contact ---------------- */

/** Raises a hand and pulses the orb once per beat. Used by both casters. */
function castBuild(book, ctx, { raise = -1.9, orbSwell = 2.2, hover = 0 }) {
  const times = beatTimes(ctx);
  const { ap, attacker } = ctx;
  const arm = [];
  const orb = [];
  for (const t of times) {
    arm.push({ t: t - 0.7, value: -0.4 }, { t: t - 0.15, value: raise }, { t: t + 0.5, value: -0.6 });
    orb.push({ t: t - 0.7, value: 1 }, { t: t - 0.15, value: orbSwell }, { t: t + 0.35, value: 1 });
  }
  book.add(ap.armR, 'rotation.x', arm, 'easeOut');
  if (ap.orb) {
    book.add(ap.orb.scale, 'x', orb, 'easeOut');
    book.add(ap.orb.scale, 'y', orb, 'easeOut');
    book.add(ap.orb.scale, 'z', orb, 'easeOut');
  }
  if (hover) {
    book.add(attacker.position, 'y', [
      { t: 0.3, value: 0 }, { t: 1.2, value: hover },
      { t: ctx.total - 1.0, value: hover }, { t: ctx.total - 0.2, value: 0 }
    ], 'easeInOut');
  }
}

// All three queen variants cast from a hover of their own, via `castBuild`'s
// `hover` option, writing `attacker.position.y` for the whole fight rather
// than only the approach and return. `battle()` checks this flag before
// adding its own approach/return glide, which would otherwise fight the
// same property with a different height and a different schedule.
const queenTear = {
  name: 'Tearing',
  lead: 2.2,
  reels: ['side', 'spin', 'stumble'],
  finishSound: 'spell',
  hover: true,
  build(book, ctx) {
    castBuild(book, ctx, { raise: -2.1, orbSwell: 2.6, hover: 0.4 });
  }
};

const queenStorm = {
  name: 'Storm',
  lead: 2.0,
  reels: ['spin', 'stumble', 'kneel'],
  finishSound: 'spell',
  hover: true,
  build(book, ctx) {
    castBuild(book, ctx, { raise: -2.5, orbSwell: 3.0, hover: 0.5 });
    // Turns on the spot as the storm builds.
    const times = beatTimes(ctx);
    book.add(ctx.attacker.userData.facing, 'rotation.y',
      times.map((t, i) => ({ t, value: (i + 1) * 0.5 })), 'easeInOut');
  }
};

/* ---------------- rook: artillery ---------------- */

/** How long a ball is in the air. Short: the squares are close together. */
const SHOT_FLIGHT = 0.26;

/**
 * The castle's gun: run out, laid, fired, and run back in.
 *
 * Each report takes one part off the enemy, so the beats the damage list hands
 * over are the shots — three of them, and then the finisher, which is the one
 * that brings the victim down.
 *
 * The ball itself is not keyframed. A timeline can only track objects that
 * exist when it is built, and the shot is spawned at the moment of firing from
 * wherever the muzzle has actually swung to — so it is launched through
 * `animator.shots`, which ticks it like the debris field, and timed to land on
 * the beat the impact is already scheduled for.
 */
const rookBombard = {
  name: 'Bombardment',
  lead: 2.4,
  reels: ['back', 'stumble', 'side'],
  finishSound: 'smash',
  build(book, ctx) {
    const times = beatTimes(ctx);
    const { ap, attacker, animator, to } = ctx;
    const cannon = ap.cannon;
    const barrel = ap.cannonBarrel;
    const flash = ap.muzzleFlash;

    const runOutAt = times[0] - 1.25;
    const runInAt = times[times.length - 1] + 0.45;

    if (cannon) {
      // Run out before the first shot, and back in once the last has landed.
      book.at(runOutAt - 0.05, () => { cannon.visible = true; });
      book.add(cannon.position, 'z', [
        { t: runOutAt, value: CANNON_STOWED },
        { t: runOutAt + 0.7, value: CANNON_RUN_OUT }
      ], 'easeOut');
      book.add(cannon.position, 'z', [
        { t: runInAt, value: CANNON_RUN_OUT },
        { t: runInAt + 0.7, value: CANNON_STOWED }
      ], 'easeInOut');
      book.at(runInAt + 0.75, () => { stowCannon(cannon); });
    }

    const recoil = [];
    const rock = [];
    const kick = [];
    const flare = [];

    times.forEach((t, i) => {
      const fire = t - SHOT_FLIGHT;

      // The gun rides back on the report and is run up again before the next.
      recoil.push(
        { t: fire - 0.10, value: CANNON_RUN_OUT },
        { t: fire + 0.08, value: CANNON_RUN_OUT - 0.075 },
        { t: fire + 0.70, value: CANNON_RUN_OUT }
      );
      // Barrel level, then kicked up by the recoil and laid again.
      kick.push(
        { t: fire - 0.10, value: Math.PI / 2 },
        { t: fire + 0.08, value: Math.PI / 2 - 0.22 },
        { t: fire + 0.75, value: Math.PI / 2 }
      );
      // The flash is brief and violent: up in two frames, gone in five.
      flare.push(
        { t: fire - 0.02, value: 0.0001 },
        { t: fire + 0.04, value: 1 },
        { t: fire + 0.16, value: 0.0001 }
      );
      // The tower itself takes the shove, lifting rather than sinking — it is
      // hovering, not planted.
      rock.push(
        { t: fire - 0.10, value: 0 },
        { t: fire + 0.10, value: 0.05 },
        { t: fire + 0.80, value: 0 }
      );

      // Launch so the ball lands exactly on the beat the impact is scheduled
      // for. The muzzle's world position is read at fire time, after the
      // barrel has swung, rather than guessed when the timeline was built.
      book.at(fire, () => {
        const muzzle = flash
          ? flash.getWorldPosition(new THREE.Vector3())
          : attacker.position.clone().setY(0.6);
        const target = animator.impactPoint(ctx.victim, ctx.beats[i]?.impact ?? 'chest', to);
        animator.shots.fire(muzzle, target, SHOT_FLIGHT);
        animator.sparks.burst(muzzle, { color: 0xffcc7a, count: 20, speed: 3.4, life: 0.35 });
      });
    });

    if (cannon) book.add(cannon.position, 'z', recoil, 'easeOut');
    if (barrel) book.add(barrel, 'rotation.x', kick, 'easeOut');
    if (flash) {
      for (const axis of ['x', 'y', 'z']) book.add(flash.scale, axis, flare, 'easeOut');
    }
    book.add(attacker.position, 'y', rock, 'easeOut');
  }
};

/* ---------------- king: sword ---------------- */

const kingCleave = {
  name: 'Cleave',
  lead: 2.2,
  reels: ['side', 'back', 'stumble'],
  finishSound: 'sword',
  build(book, ctx) {
    const times = beatTimes(ctx);
    // One-handed, alternating the arc left and right. The off hand
    // counterbalances — it swings against the cut rather than gripping the
    // hilt, because only the sword hand is closed around anything.
    swing(book, ctx.ap.armR, times, { wind: -2.6, hit: 0.75, lead: 0.6 });
    swing(book, ctx.ap.armL, times, { wind: 1.2, hit: -0.4, lead: 0.6 });
    const twist = [];
    times.forEach((t, i) => {
      twist.push({ t: t - 0.5, value: i % 2 ? -0.45 : 0.45 }, { t, value: 0 });
    });
    book.add(ctx.ap.torso, 'rotation.y', twist, 'easeOut');
    lean(book, ctx.ap, times, 0.42);
  }
};

const kingDuellist = {
  name: "Duellist's Finish",
  lead: 2.0,
  reels: ['back', 'spin', 'kneel'],
  finishSound: 'sword',
  build(book, ctx) {
    const times = beatTimes(ctx);
    // Tighter, quicker cuts from a guard position.
    swing(book, ctx.ap.armR, times, { wind: -1.6, hit: 1.1, lead: 0.4 });
    book.add(ctx.ap.armL, 'rotation.x',
      [{ t: 0.5, value: -0.9 }, { t: ctx.total - 0.5, value: -0.7 }], 'easeInOut');
    const step = [];
    times.forEach((t, i) => {
      step.push({ t: t - 0.35, value: 0 }, { t, value: i % 2 ? 0.05 : -0.05 }, { t: t + 0.4, value: 0 });
    });
    book.add(ctx.attacker.position, 'y', step, 'easeOut');
  }
};


/* ---------------- the named matchups ---------------- */

const knightBrokenLance = {
  name: 'Broken Lance',
  lead: 2.2,
  reels: ['side', 'back', 'side'],
  finishSound: 'smash',
  // The whole point of this one is the golem, so it plays only against a rook
  // and always plays there.
  requires: (attacker, victim) => victim.userData.type === 'r',
  exclusive: true,
  // The opening pass takes nothing off the golem — it breaks the lance.
  opener: {
    sound: 'crack',
    impact: 'chest',
    reel: 'graze',
    spark: { color: 0xb9b4a6, count: 28, speed: 3.2, life: 0.6 },
    attackerShed: ATTACKER_LANCE
  },
  build(book, ctx) {
    const { attacker, ap, from, approach } = ctx;
    const times = beatTimes(ctx);
    const mount = ap.mount ?? attacker;
    const first = times[0];

    // One committed charge into the stone, then a hard stop.
    book.add(attacker.position, 'x', [
      { t: 0.15, value: from.x }, { t: first - 0.9, value: from.x },
      { t: first, value: approach.x }, { t: first + 0.5, value: approach.x }
    ], 'easeIn');
    book.add(attacker.position, 'z', [
      { t: 0.15, value: from.z }, { t: first - 0.9, value: from.z },
      { t: first, value: approach.z }, { t: first + 0.5, value: approach.z }
    ], 'easeIn');

    // The lance is couched for the pass, then the arm drops uselessly.
    book.add(ap.armR, 'rotation.z', [
      { t: first - 0.8, value: 0.5 }, { t: first - 0.1, value: -0.1 },
      { t: first + 0.4, value: 0.7 }
    ], 'easeOut');

    // Afterwards it is hooves and fists: the horse rears on every later beat.
    const pitch = [{ t: first + 0.5, value: -0.2 }];
    const hop = [{ t: first + 0.5, value: 0 }];
    for (const t of times.slice(1)) {
      pitch.push({ t: t - 0.55, value: -0.8 }, { t, value: 0.3 }, { t: t + 0.45, value: -0.15 });
      hop.push({ t: t - 0.5, value: 0.3 }, { t, value: 0 }, { t: t + 0.4, value: 0.12 });
    }
    book.add(mount, 'rotation.x', pitch, 'easeOut');
    book.add(attacker.position, 'y', hop, 'easeOut');
    swing(book, ap.armL, times.slice(1), { wind: -2.0, hit: 0.8, lead: 0.5 });
    // Gallop into the stone, and only into it: once the lance is gone the
    // horse is rearing on the spot, and legs cycling under a rear look wrong.
    addHorseGait(book, attacker, 0.15, first, { kind: 'gallop', strides: 3 });
  }
};

const kingLastGuard = {
  name: 'Last Guard',
  lead: 2.0,
  reels: ['back', 'side', 'kneel'],
  finishSound: 'sword',
  requires: (attacker, victim) => victim.userData.type === 'k',
  exclusive: true,
  // The victim gets its sword up and catches the first cut. It is the only
  // defensive motion in the game, and it does not save it.
  opener: {
    sound: 'sword',
    impact: 'weapon',
    reel: 'parry',
    spark: { color: 0xfff0a0, count: 34, speed: 3.2 }
  },
  build(book, ctx) {
    const times = beatTimes(ctx);
    // A measured duel: high guard, then heavier and heavier cuts. The off
    // hand trails the sword arm and swings against it, a half-beat late.
    swing(book, ctx.ap.armR, times, { wind: -2.3, hit: 0.9, lead: 0.55 });
    swing(book, ctx.ap.armL, times.map((t) => t + 0.06), { wind: 1.0, hit: -0.35, lead: 0.55 });
    const twist = [];
    times.forEach((t, i) => {
      twist.push({ t: t - 0.5, value: i % 2 ? -0.5 : 0.5 }, { t, value: 0 });
    });
    book.add(ctx.ap.torso, 'rotation.y', twist, 'easeOut');
    lean(book, ctx.ap, times, 0.4);
  }
};

/* ---------------- one more for each attacker ---------------- */

const knightRunDown = {
  name: 'Run Down',
  lead: 2.0,
  reels: ['stumble', 'back', 'stumble'],
  finishSound: 'gallop',
  // Trampling only makes sense against something you can knock down.
  requires: (attacker, victim) => victim.userData.type !== 'r',
  // Its own gallop from the very first frame — see knightCharge above.
  ownsApproach: true,
  build(book, ctx) {
    const { attacker, ap, from, approach, to } = ctx;
    const times = beatTimes(ctx);
    const mount = ap.mount ?? attacker;
    // No wheeling and no backing off: rides straight over, again and again,
    // low and fast, trampling on each pass.
    const px = [{ t: 0.15, value: from.x }];
    const pz = [{ t: 0.15, value: from.z }];
    const hop = [{ t: 0.4, value: 0 }];
    times.forEach((t, i) => {
      const over = approach.clone().lerp(to, 0.55);
      const back = approach.clone().lerp(from, 0.3 + i * 0.05);
      px.push({ t: t - 0.7, value: back.x }, { t, value: over.x }, { t: t + 0.5, value: back.x });
      pz.push({ t: t - 0.7, value: back.z }, { t, value: over.z }, { t: t + 0.5, value: back.z });
      hop.push({ t: t - 0.35, value: 0.14 }, { t, value: 0 }, { t: t + 0.35, value: 0.08 });
    });
    book.add(attacker.position, 'x', px, 'easeIn');
    book.add(attacker.position, 'z', pz, 'easeIn');
    book.add(attacker.position, 'y', hop, 'easeOut');
    book.add(mount, 'rotation.x', times.flatMap((t) => (
      [{ t: t - 0.3, value: -0.22 }, { t, value: 0.26 }]
    )), 'easeOut');
    swing(book, ap.armR, times, { wind: -0.9, hit: 0.7, lead: 0.4 });
    addHorseGait(book, attacker, 0.15, times[times.length - 1] + 0.5,
      { kind: 'gallop', strides: Math.max(3, times.length * 2) });
  }
};


const queenAscend = {
  name: 'Ascendant',
  lead: 2.3,
  reels: ['kneel', 'back', 'stumble'],
  finishSound: 'spell',
  hover: true,
  build(book, ctx) {
    // Rises well clear of the board and works downward; the victim is pinned
    // to the ground for the whole fight.
    castBuild(book, ctx, { raise: -2.7, orbSwell: 3.2, hover: 0.85 });
    const times = beatTimes(ctx);
    book.add(ctx.ap.armL, 'rotation.x',
      times.map((t) => ({ t: t - 0.2, value: -2.2 })), 'easeOut');
    // Turns slowly overhead as it casts.
    book.add(ctx.attacker.userData.facing, 'rotation.y',
      times.map((t, i) => ({ t, value: (i + 1) * 0.28 })), 'easeInOut');
  }
};

const kingBeheading = {
  name: 'Beheading',
  lead: 1.8,
  reels: ['spin', 'back', 'kneel'],
  finishSound: 'sword',
  // Needs a head to take. The golem has a joint where its head would be, but
  // nothing on it worth cutting off.
  requires: (attacker, victim) => victim.userData.type !== 'r',
  build(book, ctx) {
    const times = beatTimes(ctx);
    // One wide horizontal cut, taken from a low wind-up, then follow-ups.
    book.add(ctx.ap.armR, 'rotation.z', [
      { t: times[0] - 0.7, value: 1.5 }, { t: times[0] - 0.05, value: -1.3 },
      { t: times[0] + 0.5, value: 0.2 }
    ], 'easeOut');
    swing(book, ctx.ap.armR, times.slice(1), { wind: -2.1, hit: 0.8, lead: 0.5 });
    book.add(ctx.ap.torso, 'rotation.y', [
      { t: times[0] - 0.7, value: 0.7 }, { t: times[0], value: -0.6 },
      { t: times[0] + 0.6, value: 0 }
    ], 'easeOut');
    lean(book, ctx.ap, times.slice(1), 0.34);
  }
};

/* ------------------------------------------------------------------ */
/* The set pieces, per attacker                                        */
/*                                                                     */
/* Declared last because the variants above are const bindings: naming */
/* them any earlier would read them before they are initialised.       */
/* ------------------------------------------------------------------ */

const BATTLES = {
  p: [pawnFlurry, pawnShieldBash, pawnLowSweep],
  n: [knightCharge, knightWheel, knightRunDown, knightBrokenLance],
  b: [bishopChapterAndVerse, bishopExcommunication, bishopCatechism],
  r: [rookBombard],
  q: [queenTear, queenStorm, queenAscend],
  k: [kingCleave, kingDuellist, kingBeheading, kingLastGuard]
};

export { Timeline };
